import moment from "moment-timezone";
import { groupBy } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiFactory from "../api";
import logo from "../assets/logo_company.jpg";
import {
  CHATTING,
  conversationTab,
  conversationType,
  conversationTypeMap,
  eventBusType,
  MESSAGE_STATUS,
  PANEL_STATUS,
} from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import {
  openNotificationError,
  openNotificationSuccess,
  openNotificationWarn,
  isImageFile,
  isVideoFile,
  isAudioFile,
} from "../utils/Utils";
import { pushNotification } from "../utils/UtilsNotification";

// ✅ RxJS stream websocket
import { searchChatEvent$ } from "../api/eventBus";
import { chatEvents$ } from "../api/webSocketRx";
import { sortConversationList } from "../utils/ConversationHelper";

const MenuContext = createContext(null);
export const useMenuContext = () => {
  return useContext(MenuContext);
};

const updateFavicon = (unreadCount) => {
  const canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 200;
  const ctx = canvas.getContext("2d");
  const size = 200;
  const cornerRadius = 50;
  const img = new Image();
  img.src = logo;
  img.crossOrigin = "anonymous";
  img.onload = () => {
    ctx.beginPath();
    ctx.moveTo(cornerRadius, 0);
    ctx.lineTo(size - cornerRadius, 0);
    ctx.quadraticCurveTo(size, 0, size, cornerRadius);
    ctx.lineTo(size, size - cornerRadius);
    ctx.quadraticCurveTo(size, size, size - cornerRadius, size);
    ctx.lineTo(cornerRadius, size);
    ctx.quadraticCurveTo(0, size, 0, size - cornerRadius);
    ctx.lineTo(0, cornerRadius);
    ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, 0, 0, size, size);
    if (unreadCount > 0) {
      ctx.fillStyle = "red";
      ctx.beginPath();
      ctx.arc(130, 130, 74, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 110px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const displayText = unreadCount > 9 ? "9+" : unreadCount;
      ctx.fillText(displayText, 130, 135);
    }
    const newFavicon = document.createElement("link");
    newFavicon.rel = "icon";
    newFavicon.href = canvas.toDataURL("image/png");
    const oldFavicon = document.querySelector("link[rel='icon']");
    if (oldFavicon) {
      document.head.removeChild(oldFavicon);
    }
    document.head.appendChild(newFavicon);
  };
};

const getFileType = (file) => {
  const fileName =
    file?.originalName || file?.fileName || file?.name || file?.mainPath || "";
  const cleanFileName = fileName.split("?")[0];
  const contentType = (file?.contentType || "").toUpperCase();

  if (
    contentType === "IMAGE" ||
    contentType.includes("IMAGE") ||
    isImageFile(cleanFileName)
  ) {
    return "IMAGE";
  }
  if (
    contentType === "VIDEO" ||
    contentType.includes("VIDEO") ||
    isVideoFile(cleanFileName) ||
    Boolean(file?.videoImgPreviewPath) ||
    Boolean(file?.videoImgResourceId)
  ) {
    return "VIDEO";
  }
  if (
    contentType === "AUDIO" ||
    contentType.includes("AUDIO") ||
    isAudioFile(cleanFileName)
  ) {
    return "AUDIO";
  }
  return "FILE";
};

const formatFileItem = (file, extra = {}) => {
  const type = getFileType(file);
  const imgUrl =
    file?.thumbnailPreviewPath ||
    file?.mainPath ||
    file?.location ||
    file?.pathUrl;

  return {
    ...file,
    id: file?.id || file?.resourceId || null,
    resourceId: file?.resourceId || file?.id || null,
    mainPath: file?.mainPath || file?.location || imgUrl,
    thumbnailPreviewPath:
      file?.thumbnailPreviewPath || file?.mainPath || imgUrl,
    videoImgPreviewPath: file?.videoImgPreviewPath,
    fileName: file?.fileName || file?.originalName || file?.name,
    originalName: file?.originalName || file?.fileName || file?.name,
    volume: file?.volume || file?.fileSize || file?.size,
    contentType: file?.contentType || type,
    createdAt: file?.createdAt || extra?.createdAt || new Date().toISOString(),
    senderId: file?.senderId || extra?.senderId,
    mediaType: type,
  };
};

export const MenuProvider = ({ children }) => {
  const { languageMap, user } = useInfoUser();
  const { selectedConversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const conversationRef = useRef(null);
  const [conversationList, setConversationList] = useState([]);
  const [conversationTs, setConversationTs] = useState(moment());
  const [isMoreConversation, setIsMoreConversation] = useState(false);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isAsRead, setIsAsRead] = useState(false);
  const [labelList, setLabelList] = useState();
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [conversationHeight, setConversationHeight] = useState(
    window.innerHeight,
  );
  /**
   * There is 4 kinds of panel status
   * 1. GROUP_INFO: when user click panel button. if user click panel button in second time -> close all panels
   * 2. MEDIA_STORAGE: when user click view all button. if user click back button-> back to GROUP_INFO
   * 3. SEARCH_CHAT: when user click search in chat button. if user click close button -> close search panel
   * 4. SEARCH_CHAT_AFTER_GROUP_INFO: when user click search in chat button after opening GROUP_INFO or MEDIA_STORAGE.
   *                                  if user click close -> back to GROUP_INFO
   */
  const [panelStatus, setPanelStatus] = useState(null);
  const [mediaStorageType, setMediaStorageType] = useState("IMAGE");
  const mediaStorageTypeRef = useRef(mediaStorageType);
  useEffect(() => {
    mediaStorageTypeRef.current = mediaStorageType;
  }, [mediaStorageType]);

  /**
   * Conversation search -> call api with this value
   * conversationSearchText -> display search input value
   */
  const [conversationSearchText, setConversationSearchText] = useState("");
  // const [searchInputValue, setSearchInputValue] = useState("");
  /* type -> filter conversation by tab (All, Personal, Group) */
  const [conversationSearch, setConversationSearch] = useState({
    labelIds: [],
    starred: null,
    textSearch: null,
    // maxTs: null,
  });

  //Contact
  const [searchConversationList, setSearchConversationList] = useState([]);
  const [lastSearchConversationTs, setLastSearchConversationTs] =
    useState(moment());
  const [isMoreSearchConversation, setIsMoreSearchConversation] =
    useState(false);
  const searchConversationRef = useRef(null);

  const [messageSearch, setMessageSearch] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const [collapseMedia, setCollapseMedia] = useState([]);
  const [openCollapses, setOpenCollapses] = useState([]);
  const [mediaStorageLoading, setMediaStorageLoading] = useState(false);
  const [loadMoreMediaStorage, setLoadMoreMediaStorage] = useState(true);
  const mediaStorageLoadingRef = useRef(false);
  const mediaStorageFirstMediaRef = useRef(null);
  const mediaStorageLastMediaRef = useRef(null);

  const appendToMediaStorage = useCallback((newFiles) => {
    if (!newFiles?.length) return;
    const today = moment().format("YYYY-MM-DD");

    setCollapseMedia((prev) => {
      const list = prev || [];
      const todayGroupIndex = list.findIndex(
        (group) => group?.key === today || group?.label === today,
      );

      if (todayGroupIndex !== -1) {
        const copy = [...list];
        const existingGroup = copy[todayGroupIndex];
        const existingKeys = new Set(
          (existingGroup.files || []).map(
            (f) => f.mainPath || f.resourceId || f.id,
          ),
        );
        const uniqueNewFiles = newFiles.filter(
          (f) => !existingKeys.has(f.mainPath || f.resourceId || f.id),
        );

        copy[todayGroupIndex] = {
          ...existingGroup,
          files: [...uniqueNewFiles, ...(existingGroup.files || [])],
        };
        return copy;
      }

      return [{ key: today, label: today, files: newFiles }, ...list];
    });

    setOpenCollapses((prev) => Array.from(new Set([today, ...(prev || [])])));
  }, []);

  const removeMediaFromStorage = useCallback((requestUuid, messageId) => {
    if (!requestUuid && !messageId) return;
    setCollapseMedia((prev) => {
      if (!prev?.length) return prev;
      return prev
        .map((group) => ({
          ...group,
          files: (group.files || []).filter(
            (f) =>
              !(
                (requestUuid &&
                  (f.requestUuid === requestUuid || f.request_uuid === requestUuid)) ||
                (messageId &&
                  (f.messageId === messageId || f.message_id === messageId))
              ),
          ),
        }))
        .filter((group) => group.files?.length > 0);
    });
  }, []);

  const [groupInfoResources, setGroupInfoResources] = useState({
    images: [],
    files: [],
    audios: [],
    videos: [],
    linkPreviews: [],
  });

  const fetchGroupInfoResources = useCallback(async () => {
    if (!selectedConversation?.conversationId) return;
    try {
      const result = await apiFactory.conversationApi.getRepresentResource(
        selectedConversation?.conversationId,
      );
      if (result?.data) {
        setGroupInfoResources(result?.data);
      }
    } catch (err) {
      console.error("Error loading group info resources:", err);
    }
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    fetchGroupInfoResources();
  }, [fetchGroupInfoResources]);

  const appendToGroupInfoResources = useCallback((rawFiles, extra = {}) => {
    if (!rawFiles?.length) return;
    const formatted = rawFiles.map((file) => formatFileItem(file, extra));

    setGroupInfoResources((prev) => {
      const next = {
        images: [...(prev?.images || [])],
        files: [...(prev?.files || [])],
        audios: [...(prev?.audios || [])],
        videos: [...(prev?.videos || [])],
        linkPreviews: [...(prev?.linkPreviews || [])],
      };

      formatted.forEach((file) => {
        const type = file.mediaType; // IMAGE, VIDEO, FILE, AUDIO
        const key =
          type === "IMAGE"
            ? "images"
            : type === "VIDEO"
              ? "videos"
              : type === "AUDIO"
                ? "audios"
                : "files";

        const currentList = next[key] || [];
        const fileKey = file.mainPath || file.resourceId || file.id;
        const exists = currentList.some(
          (f) =>
            (f.mainPath || f.resourceId || f.id || f.media?.resourceId) ===
            fileKey,
        );

        if (!exists) {
          const maxLimit = key === "images" ? 6 : 3;
          next[key] = [file, ...currentList].slice(0, maxLimit);
        }
      });
      return next;
    });
  }, []);

  const handleIncomingFiles = useCallback(
    (rawFiles, extra = {}) => {
      if (!rawFiles?.length) return;

      const formatted = rawFiles.map((file) => formatFileItem(file, extra));
      const currentType = mediaStorageTypeRef.current;
      const matchingFiles = formatted.filter(
        (f) => f.mediaType === currentType,
      );

      if (matchingFiles.length > 0) {
        appendToMediaStorage(matchingFiles);
      }

      appendToGroupInfoResources(rawFiles, extra);
    },
    [appendToMediaStorage, appendToGroupInfoResources],
  );

  const fetchMediaStorageResources = useCallback(
    async (direction = null) => {
      if (!selectedConversation?.conversationId || mediaStorageLoadingRef.current) return;

      try {
        mediaStorageLoadingRef.current = true;
        setMediaStorageLoading(true);
        const sequence =
          direction === "asc"
            ? mediaStorageFirstMediaRef.current?.sequence
            : direction === "desc"
              ? mediaStorageLastMediaRef.current?.sequence
              : null;

        const result = await apiFactory.conversationApi.getResourceList({
          conversationId: selectedConversation.conversationId,
          sequence,
          limit: 40,
          direction,
          mediaTypes: [mediaStorageType],
        });

        const normalizedResources = (result?.data || []).map((item) => ({
          ...item,
          thumbnailPreviewPath:
            item?.thumbnailPreviewPath || item?.mainPath || item?.location,
        }));

        const newGroups = Object.entries(
          groupBy(normalizedResources, "date"),
        ).map(([date, files]) => ({
          key: date,
          label: date,
          files,
        }));

        const resources = normalizedResources;

        if (!direction) {
          setCollapseMedia(newGroups);
          setOpenCollapses(newGroups.map(({ key }) => key));
          mediaStorageFirstMediaRef.current = resources[0] || null;
          mediaStorageLastMediaRef.current = resources[resources.length - 1] || null;
          setLoadMoreMediaStorage(resources.length >= 40);
        } else {
          setCollapseMedia((prev) => {
            const groups = new Map((prev || []).map((item) => [item.key, item]));

            newGroups.forEach((group) => {
              const previous = groups.get(group.key);
              const existingFiles = previous?.files || [];
              const existingKeys = new Set(
                existingFiles.map((f) => f.mainPath || f.resourceId || f.id),
              );
              const uniqueIncoming = group.files.filter(
                (f) => !existingKeys.has(f.mainPath || f.resourceId || f.id),
              );

              const files =
                direction === "asc"
                  ? [...uniqueIncoming, ...existingFiles]
                  : [...existingFiles, ...uniqueIncoming];
              groups.set(group.key, { ...group, files });
            });

            const incomingKeys = new Set(newGroups.map(({ key }) => key));
            const existing = (prev || []).filter(
              (group) => !incomingKeys.has(group.key),
            );
            const merged = newGroups.map((group) => groups.get(group.key));
            return direction === "asc"
              ? [...merged, ...existing]
              : [...existing, ...merged];
          });

          setOpenCollapses((prev) => [
            ...new Set([
              ...(prev || []),
              ...newGroups.map(({ key }) => key),
            ]),
          ]);

          if (direction === "asc") {
            mediaStorageFirstMediaRef.current = resources[0] || null;
          } else {
            mediaStorageLastMediaRef.current = resources[resources.length - 1] || null;
          }

          if (resources.length < 40) setLoadMoreMediaStorage(false);
        }
      } catch (error) {
        console.error("Error load media storage resources:", error);
      } finally {
        mediaStorageLoadingRef.current = false;
        setMediaStorageLoading(false);
      }
    },
    [
      selectedConversation?.conversationId,
      mediaStorageType,
    ],
  );

  useEffect(() => {
    setCollapseMedia([]);
    setOpenCollapses([]);
    setLoadMoreMediaStorage(true);
    mediaStorageFirstMediaRef.current = null;
    mediaStorageLastMediaRef.current = null;
  }, [mediaStorageType, selectedConversation?.conversationId]);

  /**
   * store more selectedMailId and selectedMessageId to when click chat or message search conversation list
   * -> highline converastion list
   */
  const [selectedMailId, setSelectedMailId] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);

  const [quotation, setQuotation] = useState(null);

  const [loading, setLoading] = useState(false);
  const [lastConversationTs, setLastConversationTs] = useState(null);
  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [isMessageSearch, setIsMessageSearch] = useState(false);
  const [isMenu, setIsMenu] = useState(false);
  const [labelSelectedId, setLabelSelectedId] = useState(null);
  const [isModalAddMember, setIsModalAddMember] = useState(false);
  const [isModalAddCC, setIsModalAddCC] = useState(false);
  const [isOpenModalConfirm, setIsOpenModalConfirm] = useState(false);
  const [totalNotificationItem, setTotalNotificationItem] = useState(0);
  const [isOpenModalConfirmSaveEmail, setIsOpenModalConfirmSaveEmail] =
    useState(false);
  const [isDiffHtmlMail, setIsDiffHtmlMail] = useState(false);
  const [isModalLabelGroup, setIsModalLabelGroup] = useState(false);
  const [totalUnreadHidden, setTotalUnreadHidden] = useState(0);
  const [activeKey, setActiveKey] = useState(conversationTab.ALL);

  const activeKeyRef = useRef(activeKey);

  const [isSearching, setIsSearching] = useState(false);

  const [personalDetail, setPersonalDetail] = useState(null);

  const [keydownMenuOn, setKeydownMenuOn] = useState(true);

  const handleUpdateConversationList = useCallback(
    (updatedData, isSearch = false) => {
      setConversationList((prevList) => {
        if (Array.isArray(updatedData)) {
          return sortConversationList([...updatedData], isSearch);
        }

        const index = prevList.findIndex(
          (item) => item.conversationId === updatedData?.conversationId,
        );

        if (index !== -1) {
          const newList = [...prevList];
          newList[index] = {
            ...newList[index],
            ...updatedData,
          };
          return sortConversationList(newList);
        } else {
          return sortConversationList([updatedData, ...prevList]);
        }
      });
    },
    [],
  );

  const resetAndReloadConversations = async () => {
    setIsConversationsLoading(true);
    setConversationList([]);
    setLastConversationTs(null);
    setIsMoreConversation(false);

    try {
      const request = {
        limit: CHATTING.CONVERSATION_LIMIT,
        maxTs: null,
        search: {
          ...conversationSearch,
        },
      };

      const result = await apiFactory.userConversationApi.getList(request);

      if (result?.status === 200 && result?.data) {
        handleUpdateConversationList(result?.data);
        getUnread(result?.data);

        if (result?.data.length > 0) {
          const lastItem = result?.data[result?.data.length - 1];

          setLastConversationTs(lastItem.lastMessageTs);

          setIsMoreConversation(
            result?.data?.length === CHATTING.CONVERSATION_LIMIT,
          );
        } else {
          setIsMoreConversation(false);
        }
      }
    } catch (error) {
      console.error("Reset conversation error:", error);
    } finally {
      setIsConversationsLoading(false);
    }
  };

  const handleReloadConversation = () => {
    setConversationSearch({
      labelIds: [],
      types: [],
      isUnread: false,
      starred: null,
    });

    if (conversationRef.current) {
      conversationRef.current.scrollTop = 0;
    }

    if (searchConversationRef.current) {
      searchConversationRef.current.scrollTop = 0;
    }

    setActiveKey("ALL");
    navigate("/conversation");
  };

  const onBackToConversations = () => {
    navigate("/conversation");
  };

  const calculateConversationListHeight = () => {
    /**
     * Search conversation height = 32
     * Label height = 35
     * Conversation tab height = 85
     * last 35 is to shorter than real scroll height
     */
    if (isSearching) {
      // if (activeKey === conversationTab.CONTACT_MESSAGE) {
      //   setConversationHeight(windowHeight - 32 - 85 - 30 - 42);
      // } else {
      //   setConversationHeight(windowHeight - 32 - 85 - 30);
      // }
      // setConversationHeight(windowHeight - 32 - 85 - 30);
      setConversationHeight(windowHeight - 32 - 35 - 85 - 30);
    } else {
      setConversationHeight(windowHeight - 32 - 35 - 85 - 30);
    }
  };

  /**
   * request body:
   * 1. limit
   * 2. maxTs
   * 3. search: {
   *      labelIds: [],
   *      type: [],
   *      isUnread: false,
   *      starred: null,
   *      textSearch: null,
   *      isNotification: false,
   *    }
   */
  const fetchConversationsData = async (request) => {
    setIsConversationsLoading(true);

    const result = await apiFactory.userConversationApi.getList(request);
    if (result?.status !== 200) return;

    handleUpdateConversationList(result?.data, request?.isSearch);
    getUnread(result?.data);

    if (result?.data?.length > 0) {
      setLastConversationTs(
        result?.data[result?.data?.length - 1].lastMessageTs,
      );
      setIsMoreConversation(
        result?.data?.length === CHATTING.CONVERSATION_LIMIT,
      );
    } else {
      setIsMoreConversation(false);
    }
    setIsConversationsLoading(false);
  };

  const fetchConversationsSearchMessageData = async (request) => {
    setIsConversationsLoading(true);
    const result =
      await apiFactory.conversationApi.getListMessageSearch(request);

    if (result?.status !== 200) return;

    const conversationList = Array.from(
      new Map([...result?.data].map((i) => [i.messageId, i])).values(),
    );

    handleUpdateConversationList(conversationList);

    if (result?.data?.length > 0) {
      setLastConversationTs(result?.data[result?.data?.length - 1].createdAt);

      setIsMoreConversation(
        result?.data?.length === CHATTING.CONVERSATION_LIMIT,
      );
    } else {
      setIsMoreConversation(false);
    }
    setIsConversationsLoading(false);
  };

  const fetchConversationsSearchMailData = async (request) => {
    if (
      !request?.search?.searchContent ||
      request?.search?.searchContent?.trim().length === 0
    )
      return;

    const result = await apiFactory.userMailApi.getPage(request);

    if (result?.status !== 200) return;

    if (result?.data?.items?.length > 0) {
      setConversationList([...result?.data?.items]);
      setLastConversationTs(
        result?.data?.items[result?.data?.items?.length - 1].createdAt,
      );

      setIsMoreConversation(
        result?.data?.items?.length === CHATTING.CONVERSATION_LIMIT,
      );
    } else {
      setConversationList([]);
      setIsMoreConversation(false);
    }

    setIsConversationsLoading(false);
  };

  const fetchConversationsSearchLetterData = async (request) => {
    if (
      !request?.search?.searchContent ||
      request?.search?.searchContent?.trim().length === 0
    )
      return;

    const result = await apiFactory.letterApi.getPage(request);

    if (result?.status !== 200) return;

    if (result?.data?.items?.length > 0) {
      setConversationList([...result?.data?.items]);
      setLastConversationTs(
        result?.data?.items[result?.data?.items?.length - 1].createdAt,
      );

      setIsMoreConversation(
        result?.data?.items?.length === CHATTING.CONVERSATION_LIMIT,
      );
    } else {
      setConversationList([]);
      setIsMoreConversation(false);
    }

    setIsConversationsLoading(false);
  };

  const fetchConversationsSearchLetterContact = async (request) => {
    setIsConversationsLoading(true);

    const result = await apiFactory.userConversationApi.searchLetter(request);
    if (result?.status !== 200) return;

    handleUpdateConversationList(result?.data, request?.isSearch);
    getUnread(result?.data);

    if (result?.data?.length > 0) {
      setLastConversationTs(
        result?.data[result?.data?.length - 1].lastMessageTs,
      );
      setIsMoreConversation(
        result?.data?.length === CHATTING.CONVERSATION_LIMIT,
      );
    } else {
      setIsMoreConversation(false);
    }
    setIsConversationsLoading(false);
  };

  const fetchMoreConversations = async () => {
    if (isConversationsLoading || !isMoreConversation) return;

    setIsConversationsLoading(true);

    if (!conversationList || conversationList?.length === 0) {
      setIsConversationsLoading(false);
      return;
    }

    const request = {
      limit: CHATTING.CONVERSATION_LIMIT,
      search: { ...conversationSearch },
      isSearch: conversationTab.CONTACT_CHAT === activeKey,
    };

    const pinnedConversationTs =
      conversationList?.[conversationList?.length - 1]?.pinnedConversationTs;

    if (pinnedConversationTs) {
      request.search.lastPinnedTs = pinnedConversationTs;
    } else {
      request.search.isPersonal = request?.search?.isSearch
        ? null
        : conversationList?.[conversationList?.length - 1]?.type ===
        conversationType.PERSONAL;
      request.search.maxTs =
        conversationList?.[conversationList?.length - 1]?.lastMessageAt;
    }

    const result = await apiFactory.userConversationApi.getList(request);

    if (result?.data && result?.data?.length > 0) {
      const mergedList = [...conversationList, ...result.data];
      const uniqueList = Array.from(
        new Map(
          mergedList.map((item) => [item?.conversationId, item]),
        ).values(),
      );

      getUnread(uniqueList);
      handleUpdateConversationList(uniqueList, request.isSearch);

      setLastConversationTs(result.data[result.data.length - 1].lastMessageTs);
    }
    setIsMoreConversation(result?.data?.length === CHATTING.CONVERSATION_LIMIT);
    setIsConversationsLoading(false);
  };

  const fetchMoreSearchChat = async () => {
    if (isConversationsLoading || !isMoreConversation) return;

    setIsConversationsLoading(true);
    const result = await apiFactory.conversationApi.getListMessageSearch({
      limit: CHATTING.CONVERSATION_LIMIT,
      maxTs: conversationList?.[conversationList?.length - 1].createdAt,
      search: { searchContent: conversationSearch?.textSearch },
    });

    const data = result?.data ?? [];
    if (data.length) {
      const newConversationList = Array.from(
        new Map(
          [...conversationList, ...data].map((i) => [i.messageId, i]),
        ).values(),
      );

      handleUpdateConversationList(newConversationList);

      if (data.length < CHATTING.CONVERSATION_LIMIT) {
        setIsMoreConversation(false);
      }
    } else {
      setIsMoreConversation(false);
    }

    setIsConversationsLoading(false);
  };

  const fetchMoreMailConversations = async (type) => {
    if (
      !conversationSearch?.textSearch ||
      conversationSearch?.textSearch?.trim().length === 0
    )
      return;

    const result = await apiFactory.userMailApi.getPage({
      limit: CHATTING.CONVERSATION_LIMIT,
      maxTs: conversationList[conversationList?.length - 1]?.createdAt,
      direction: "DESC",
      search: {
        searchContent: conversationSearch?.textSearch,
        isHost: type === conversationTab.SEARCH_MESSAGE_SEND,
      },
    });

    const data = result?.data?.items ?? [];
    if (data.length) {
      setConversationList((prev) => [...prev, ...data]);
    }
  };

  const fetchMoreLetterContact = async () => {
    const result = await apiFactory.userConversationApi.searchLetter({
      limit: CHATTING.CONVERSATION_LIMIT,
      maxTs: conversationList[conversationList?.length - 1]?.lastMessageTs,
      direction: "DESC",
      search: {
        personName: conversationSearch?.textSearch,
        isNotification: activeKey !== conversationTab.CONTACT_MESSAGE,
      },
    });

    if (result?.data?.length) {
      // handleUpdateConversationList(result?.data, true);
      setConversationList((prev) => {
        return [...prev, ...(result?.data ?? [])];
      });

      getUnread(result?.data);

      if (result?.data?.length < CHATTING.CONVERSATION_LIMIT) {
        setIsMoreConversation(false);
      }
    } else {
      setIsMoreConversation(false);
    }

    setIsConversationsLoading(false);
  };

  const showModalLabel = () => {
    setIsModalLabelGroup(true);
    setIsMenu(false);
  };

  const changeUnread = (conversation) => {
    if (conversation?.type === "NOTIFICATION") {
      apiFactory.letterApi.changeUnread({
        conversationId: conversation?.conversationId,
      });
    } else {
      apiFactory.conversationApi.changeUnread({
        conversationId: conversation?.conversationId,
      });
    }
  };

  const seenReaction = (userConversationId) => {
    if (!userConversationId) return;

    apiFactory.conversationApi.seenReaction({
      userConversationId: userConversationId,
    });
  };

  const onChangeUnreadConversation = (conversation) => {
    if (!conversation) return;

    const isUnread = conversation.unreadCount > 0;
    const hasReactionNotSeen = conversation.seenReaction === false;

    if (isUnread) changeUnread(conversation);
    if (hasReactionNotSeen) seenReaction(conversation.userConversationId);

    setConversationList((prevList) => {
      if (activeKeyRef.current === conversationTab.UNREAD) {
        return prevList.filter(
          (item) => item.conversationId !== conversation.conversationId,
        );
      }

      return prevList.map((item) => {
        if (item.conversationId === conversation.conversationId) {
          return {
            ...item,
            unreadCount: 0, // Reset về 0
            reactionPreview: hasReactionNotSeen ? null : item.reactionPreview,
            seenReaction: hasReactionNotSeen ? true : item.seenReaction,
          };
        }
        return item;
      });
    });
  };

  const onChangeConversation = useCallback(
    (conversation) => {
      if (activeKey === conversationTab.SEARCH_CHAT) {
        setSelectedMessageId(conversation?.messageId);
      }

      if (
        [
          conversationTab.SEARCH_MESSAGE_RECEIVE,
          conversationTab.SEARCH_MESSAGE_SEND,
        ].includes(activeKey)
      ) {
        setSelectedMailId(conversation?.mailId);
      }

      if (
        [
          conversationTab.NEW_SEARCH_MESSAGE_RECEIVE,
          conversationTab.NEW_SEARCH_MESSAGE_SEND,
        ].includes(activeKey)
      ) {
        setSelectedMailId(conversation?.letterId);
      }

      if (conversation?.conversationId === selectedConversation?.conversationId)
        return;

      handleChangeConversation(conversation);
    },
    [
      activeKey,
      selectedConversation,
      conversationList,
      conversationTab,
      isDiffHtmlMail,
      labelSelectedId,
    ],
  );

  const jumpToSearchChat = (conversation) => {
    setSelectedMessageId(conversation?.messageId);

    if (conversation?.conversationId === selectedConversation?.conversationId) {
      navigate(
        `/conversation/chat/${conversation?.conversationId}?jumpMessageId=${conversation?.messageId}`,
      );

      return;
    }

    onChangeUnreadConversation(conversation);

    navigate(
      `/conversation/chat/${conversation?.conversationId}?jumpMessageId=${conversation?.messageId}`,
    );
    setQuotation(null);

    searchChatEvent$.next({
      type: eventBusType.SEARCH_CHAT,
      payload: conversation,
    });
  };

  const handleChangeConversation = (conversation) => {
    onChangeUnreadConversation(conversation);

    if (
      [
        conversationType.PERSONAL,
        conversationType.GROUP,
        conversationType.SYSTEM,
        conversationType.SELF,
      ].includes(conversation?.type)
    ) {
      navigate(`/conversation/chat/${conversation?.conversationId}`);
    }

    if (
      [conversationType.DRAFT, conversationType.SCHEDULE].includes(
        conversation?.type,
      )
    ) {
      navigate(`/conversation/draft-mail/${conversation?.conversationId}`);
    }

    if (
      conversation?.type === conversationType.NOTIFICATION &&
      conversation?.version === 2
    ) {
      navigate(`/conversation/new-mail/${conversation?.conversationId}`);
    }

    if (
      conversation?.type === conversationType.NOTIFICATION &&
      !conversation?.version
    ) {
      navigate(`/conversation/mail/${conversation?.conversationId}`);
    }

    setQuotation(null);
  };

  const onChangeConversationBeforeCloseModalSavedEmail = (conversation) => {
    setIsOpenModalConfirmSaveEmail(false);
    handleChangeConversation(conversation);
    setQuotation(null);
  };

  const onChangeConversationTab = (key) => {
    if (activeKey === key) return;

    setActiveKey(key);
    setConversationList([]);
    setIsMoreConversation(true);
    setConversationSearch({
      ...conversationSearch,
      ...conversationTypeMap[key],
    });
  };

  const updateConversationList = (
    basicConversationInfo,
    announcer,
    message,
  ) => {
    if (!basicConversationInfo?.conversationId) return;

    if (
      user?.userId === announcer?.userId &&
      message?.contentType === MESSAGE_STATUS.CREATE_PERSONAL
    ) {
      return;
    }

    setConversationList((prev) => {
      const old = prev.find(
        (x) => x?.conversationId === basicConversationInfo?.conversationId,
      );

      const newInfo = {
        ...old,
        ...basicConversationInfo,
        alarm: old?.alarm,
        members: basicConversationInfo?.members || old?.members,
        totalMember:
          basicConversationInfo?.members?.length ||
          old?.totalMember ||
          old?.members?.length,
        pinnedConversationTs: old?.pinnedConversationTs || null,
      };

      if (user?.userId !== announcer?.userId) {
        if (selectedConversation?.conversationId === newInfo?.conversationId) {
          newInfo.unreadCount = 0;
          changeUnread(selectedConversation);
        } else {
          newInfo.unreadCount = (old?.unreadCount || 0) + 1;
        }
      }

      const filtered = prev.filter(
        (x) =>
          x?.conversationId !== newInfo?.conversationId &&
          x?.conversationId !== basicConversationInfo?.draftConversationId,
      );

      return sortConversationList([...filtered, newInfo]);
    });
  };

  const updateMarkRead = () => {
    setConversationList((prevList) => {
      const hasUnread = prevList.some((c) => c.unreadCount > 0);
      if (!hasUnread) return prevList;

      const updatedList = prevList.map((c) => ({
        ...c,
        unreadCount: 0,
      }));

      return updatedList;
    });
  };

  const updateReactionPreview = (conversationId, reactionPreview) => {
    if (reactionPreview?.reactionType === "CANCEL") return;

    if (
      selectedConversation?.conversationId === conversationId &&
      selectedConversation?.userConversationId
    ) {
      seenReaction(selectedConversation.userConversationId);
      return;
    }

    setConversationList((prevList) => {
      const updatedList = prevList.map((c) =>
        c.conversationId === conversationId
          ? {
            ...c,
            seenReaction: false,
            reactionPreview: { ...reactionPreview },
          }
          : c,
      );

      return sortConversationList(updatedList);
    });
  };

  const updateConversationListAfterSharing = (
    basicConversationInfos,
    announcer,
  ) => {
    if (!basicConversationInfos?.length) return;

    let shouldChangeUnread = false;

    const currentTab = activeKeyRef.current;

    setConversationList((prevList) => {
      const conversationMap = new Map(
        prevList.map((item) => [item.conversationId, item]),
      );

      for (const incoming of basicConversationInfos) {
        const isMatchingTab = checkIsMatchingActiveKey(
          incoming,
          currentTab,
          announcer,
        );
        const isExistInList = conversationMap.has(incoming?.conversationId);

        if (!isMatchingTab && !isExistInList) {
          continue;
        }

        const oldItem = conversationMap.get(incoming?.conversationId);

        if (user?.userId !== announcer?.userId) {
          if (
            selectedConversation?.conversationId === incoming?.conversationId
          ) {
            incoming.unreadCount = 0;
            shouldChangeUnread = true;
          } else {
            incoming.unreadCount = (oldItem?.unreadCount || 0) + 1;
          }
        }

        conversationMap.set(incoming?.conversationId, {
          ...oldItem,
          ...incoming,
          members: incoming?.members ?? oldItem?.members,
          pinnedConversationTs:
            incoming?.pinnedConversationTs ?? oldItem?.pinnedConversationTs,
        });
      }

      return sortConversationList(Array.from(conversationMap.values()));
    });

    if (shouldChangeUnread && selectedConversation) {
      changeUnread(selectedConversation);
    }
  };

  const refreshChatMessage = () => {
    setIsMessageSearch(false);
    setMessageSearch({
      ...messageSearch,
      content: "",
    });
    setIsMenu(false);
  };

  const refreshMailMessage = () => {
    setIsMessageSearch(false);
    setMessageSearch({
      content: null,
    });
    setIsMenu(false);
  };

  const onCloseChatMessage = () => {
    setIsMessageSearch(false);

    if (messageSearch?.content?.trim()?.length > 0) {
      setMessageSearch({
        ...messageSearch,
        content: "",
      });
    }

    setIsMenu(false);
  };

  const onSearch = () => {
    setPanelStatus((prev) => {
      if (prev === PANEL_STATUS.SEARCH_CHAT) {
        return null;
      }

      if (prev === PANEL_STATUS.SEARCH_CHAT_AFTER_GROUP_INFO) {
        return PANEL_STATUS.GROUP_INFO;
      }

      if (
        [PANEL_STATUS.GROUP_INFO, PANEL_STATUS.MEDIA_STORAGE].includes(prev)
      ) {
        return PANEL_STATUS.SEARCH_CHAT_AFTER_GROUP_INFO;
      }

      return PANEL_STATUS.SEARCH_CHAT;
    });
  };

  const onChangeLabel = async (value, userConversationId) => {
    if (value === labelSelectedId || !userConversationId) return;

    let request = {
      labelId: value,
      userConversationId,
    };

    const result = await apiFactory.labelApi.bindingLabel(request);

    if (result?.status !== 200 || !result?.data) {
      return openNotificationError(result?.message);
    }

    setLabelSelectedId(value);

    const newConvs = conversationList?.map((cv) =>
      cv?.userConversationId === userConversationId
        ? { ...cv, labelId: value }
        : cv,
    );

    handleUpdateConversationList(newConvs);
  };

  const onChangeStar = async (userConversationId) => {
    const result =
      await apiFactory.conversationApi.changeStar(userConversationId);

    if (result?.status !== 200 || !result?.data) {
      return openNotificationError(result?.message);
    }

    const newConvs = conversationList?.map((cv) =>
      cv?.userConversationId === userConversationId
        ? {
          ...cv,
          starred: result?.data?.starred,
        }
        : cv,
    );

    handleUpdateConversationList(newConvs);
  };

  const openAddMemberModal = () => {
    setIsModalAddMember(true);
    setIsMenu(false);
  };

  const showModalAddCC = () => {
    setIsModalAddCC(true);
    setIsMenu(false);
  };

  const openModalConfirm = () => {
    setIsOpenModalConfirm(true);
    setIsMenu(false);
  };

  const getConversation = async () => {
    if (!selectedConversationId) {
      return;
    }

    try {
      const result = await apiFactory.conversationApi.getConversation(
        selectedConversationId,
      );

      if (result?.status !== 200) {
        navigate("/conversation");
        return;
      }

      setLabelSelectedId(result?.data?.labelId || null);
      setSelectedConversation(result?.data);
    } catch (error) {
      navigate("/conversation");
      return;
    }
  };

  const filterEventToUpdateConversationList = (eventData) => {
    const { basicConversationInfo, announcer, message } = eventData || {};
    if (!basicConversationInfo) return;

    const currentTab = activeKeyRef.current;

    const isMatchingTab = checkIsMatchingActiveKey(
      basicConversationInfo,
      currentTab,
      announcer,
    );

    const isExistInList = conversationList.some(
      (conv) => conv.conversationId === basicConversationInfo.conversationId,
    );

    if (isMatchingTab || isExistInList) {
      updateConversationList(basicConversationInfo, announcer, message);
    }
  };

  const handleRemovedMeFromGroup = (eventData) => {
    const { basicConversationInfo, message } = eventData || {};
    const conversationIdToRemove = message?.conversationId;
    const selected = selectedConversation;

    const newConversationList = conversationList.filter(
      (conversation) => conversation?.conversationId !== conversationIdToRemove,
    );

    handleUpdateConversationList(newConversationList);
  };

  const handleConversationDeleted = (eventData) => {
    const { message } = eventData || {};
    const conversationIdToDelete = message?.conversationId;
    const messageText = message?.message?.trim();

    if (!conversationIdToDelete) return;

    const newConversationList = conversationList.filter(
      (conversation) => conversation?.conversationId !== conversationIdToDelete,
    );

    handleUpdateConversationList(newConversationList);

    if (messageText?.length > 0) {
      openNotificationWarn(messageText);
      pushNotification(
        messageText,
        conversationIdToDelete || null,
        languageMap,
      );
    }

    const selected = selectedConversation;
    if (selected?.conversationId === conversationIdToDelete) {
      navigate("/conversation");
    }
  };

  const handleExitGroup = (eventData) => {
    const { basicConversationInfo, message } = eventData || {};
    const removedUserIds = message?.removedUserIds || [];
    const isMeExitGroup = removedUserIds.includes(user?.userId);

    if (isMeExitGroup) {
      handleConversationDeleted(eventData);
      return;
    }

    const conversationId = basicConversationInfo?.conversationId;
    if (!conversationId) return;

    const selected = selectedConversation;

    if (selected?.conversationId === conversationId && basicConversationInfo) {
      setSelectedConversation((prev) => ({
        ...prev,
        members: basicConversationInfo?.members,
        totalMember: basicConversationInfo?.totalMember,
        lastMessage: message?.content,
        lastMessageType: MESSAGE_STATUS.EXIT_GROUP,
        lastMessageTs: basicConversationInfo?.lastMessageTs,
        lastSenderId: basicConversationInfo?.lastSenderId,
      }));
    }

    const index = conversationList.findIndex(
      (item) => item?.conversationId === conversationId,
    );
    if (index === -1) return;

    const updated = {
      ...conversationList[index],
      members: basicConversationInfo?.members,
      totalMember: basicConversationInfo?.totalMember,
      lastMessageTs: basicConversationInfo?.lastMessageTs,
      lastSenderId: basicConversationInfo?.lastSenderId,
      lastMessage: message?.content,
      lastMessageType: MESSAGE_STATUS.EXIT_GROUP,
      unreadCount: (conversationList[index].unreadCount || 0) + 1,
    };

    const next = [...conversationList];
    next[index] = updated;

    handleUpdateConversationList(next);
  };

  const handleUpdateReaction = (eventData) => {
    const { message } = eventData || {};

    if (message?.senderId !== user?.userId) return;

    setConversationList((prevList) => {
      const index = prevList.findIndex(
        (item) => item.conversationId === message?.conversationId,
      );

      if (index !== -1) {
        const [reactorId, reactionType] = Object.entries(
          message?.respondent,
        )[0];

        prevList[index].reactionPreview = {
          reactorId: reactorId,
          reactionType: reactionType,
          requestUuid: message?.requestUuid,
        };
      }

      return [...prevList];
    });
  };

  const handleUpdateEventFile = (eventData) => {
    filterEventToUpdateConversationList(eventData);
  };

  /**
   * Cases disallow to show up desktop notification
   * 1. SEEN_MESSAGE
   * 2. TYPING
   * 3. MARK_READ
   * 4. USER_STATUS_CHANGED
   * 5. doesnt have announcer
   * 6. my message
   * 7. receive from turn off conversation(eventData?.basicConversationInfo?.alarm)
   * Note to check share message -> basicConversationInfos and messages
   */
  const postMessageWebview = (eventData, contentType) => {
    const isSendByMe = eventData?.announcer?.userId === user?.userId;

    if (eventData?.message) {
      if (
        [
          MESSAGE_STATUS.SEEN_MESSAGE,
          MESSAGE_STATUS.TYPING,
          MESSAGE_STATUS.MARK_READ,
          MESSAGE_STATUS.USER_STATUS_CHANGED,
        ].includes(contentType) ||
        !eventData?.announcer ||
        isSendByMe ||
        !eventData?.basicConversationInfo?.alarm
      ) {
        return;
      }
    } else {
      if (contentType === "LETTER") {
        eventData.message = eventData?.letter;
      } else {
        /** else is share message -> post 1 element*/
        const basicConversationInfoPresence =
          eventData?.basicConversationInfos?.find((b) => b?.alarm);
        if (!basicConversationInfoPresence) return;

        const messagePresence = eventData?.messages?.find(
          (e) =>
            e?.conversationId === basicConversationInfoPresence?.conversationId,
        );
        eventData.basicConversationInfo = basicConversationInfoPresence;
        eventData.message = messagePresence;
      }
    }

    eventData && window?.chrome?.webview?.postMessage(eventData);
  };

  const handleSeenMessage = (eventData) => {
    const seen = eventData?.seenMessageTs;

    if (!seen || seen.seenUserId !== user?.userId) return;

    setConversationList((prev) => {
      const index = prev.findIndex(
        (conversation) => conversation?.conversationId === seen.conversationId,
      );

      if (index === -1) return prev;

      const next = [...prev];

      next[index] = {
        ...next[index],
        unreadCount: 0,
      };

      return next;
    });
  };

  const processEventData = (eventData) => {
    if (!eventData) return;
    if (eventData?.event === "session-uuid") return;

    // Check if event has mail/letter files for current conversation
    const mailItem = eventData?.letter || eventData?.mail;
    if (mailItem) {
      const mailConvId =
        mailItem?.conversationId ||
        eventData?.basicConversationInfo?.conversationId ||
        eventData?.seenMessageTs?.conversationId;
      if (mailConvId && mailConvId === selectedConversation?.conversationId) {
        const mailFiles = mailItem?.resources || mailItem?.files || [];
        if (mailFiles.length > 0) {
          handleIncomingFiles(mailFiles, {
            createdAt: mailItem?.createdAt,
            senderId: mailItem?.senderId,
          });
        }
        fetchGroupInfoResources();
      }
    }

    const {
      basicConversationInfo,
      announcer,
      message,
      type,
      basicConversationInfos,
      reactionPreview,
    } = eventData || {};

    const conversationId = basicConversationInfo?.conversationId;

    postMessageWebview(eventData, type);

    switch (type) {
      case MESSAGE_STATUS.SEEN_MESSAGE: {
        handleSeenMessage(eventData);
        break;
      }

      case MESSAGE_STATUS.MARK_READ:
        updateMarkRead();
        break;

      case MESSAGE_STATUS.REACTION_PREVIEW:
        updateReactionPreview(conversationId, reactionPreview);
        break;

      case MESSAGE_STATUS.CREATE_GROUP:
        filterEventToUpdateConversationList(eventData);
        break;

      case MESSAGE_STATUS.REMOVE_MEMBER:
        if (message?.removedUserIds?.includes(user?.userId)) {
          handleRemovedMeFromGroup(eventData);
        } else {
          filterEventToUpdateConversationList(eventData);
        }
        break;

      case MESSAGE_STATUS.DELETE_GROUP:
        handleConversationDeleted(eventData);
        break;

      case MESSAGE_STATUS.EXIT_GROUP:
        handleExitGroup(eventData);
        break;

      case MESSAGE_STATUS.FILE:
        handleUpdateEventFile(eventData);
        break;

      case MESSAGE_STATUS.REACTION_MESSAGE:
        handleUpdateReaction(eventData);
        break;

      default: {
        if (type === MESSAGE_STATUS.SHARE_MESSAGE && basicConversationInfos) {
          updateConversationListAfterSharing(basicConversationInfos, announcer);
        }

        if (
          ![
            MESSAGE_STATUS.TYPING,
            MESSAGE_STATUS.SEEN_MESSAGE,
            MESSAGE_STATUS.SHARE_MESSAGE,
            MESSAGE_STATUS.REACTION_MESSAGE,
          ].includes(type)
        ) {
          filterEventToUpdateConversationList(eventData);
        }

        break;
      }
    }
  };

  const markAsRead = async () => {
    const result = await apiFactory.userConversationApi.markAsRead();

    if (result?.status !== 200) {
      openNotificationError("error");
    } else {
      updateMarkRead();
      openNotificationSuccess("successfully");
      setTotalUnreadHidden(0);
    }

    setIsAsRead(false);
  };

  const getUnread = async (conversationList) => {
    const result = await apiFactory.userConversationApi.getUnread();

    if (result?.status !== 200) {
      openNotificationError("error");
    } else {
      const unreadDisplay = conversationList?.reduce((total, conversation) => {
        return total + (conversation?.unreadCount || 0);
      }, 0);
      setTotalUnreadHidden(result?.data - unreadDisplay);
    }
  };

  const onFocusConversationSearch = () => {
    if (isSearching) return;

    setActiveKey(conversationTab.CONTACT_CHAT);
    setConversationList([]);
    setIsSearching(true);
    setConversationSearch({
      ...conversationSearch,
      ...conversationTypeMap[conversationTab.CONTACT_CHAT],
    });
  };

  const onUnFocusConversationSearch = () => {
    setActiveKey(conversationTab.ALL);
    setConversationList([]);
    setIsSearching(false);
    setConversationSearch({
      ...conversationSearch,
      ...conversationTypeMap[conversationTab.ALL],
      textSearch: null,
    });
  };

  const checkIsMatchingActiveKey = useCallback(
    (conversation, currentActiveKey, announcer) => {
      const { type, role } = conversation;

      switch (currentActiveKey) {
        case conversationTab.ALL:
          return true;

        case conversationTab.CHAT:
          return [
            conversationType.PERSONAL,
            conversationType.GROUP,
            conversationType.SYSTEM,
            conversationType.SELF,
          ].includes(type);

        case conversationTab.PERSONAL:
          return type === conversationType.PERSONAL;

        case conversationTab.NOTICE:
          return type === conversationType.NOTIFICATION;

        case conversationTab.MESSAGE_RECEIVE:
          return type === conversationType.NOTIFICATION && role !== "HOST";

        case conversationTab.MESSAGE_SEND:
          return type === conversationType.NOTIFICATION && role === "HOST";

        case conversationTab.UNREAD:
          return (
            announcer?.userId !== user?.userId && conversation?.unreadCount > 0
          );

        case conversationTab.SCHEDULE:
          return type === conversationType.SCHEDULE;

        case conversationTab.CONTACT_CHAT:
        case conversationTab.CONTACT_MESSAGE:
        case conversationTab.SEARCH_NOTICE:
        case conversationTab.SEARCH_CHAT:
        case conversationTab.SEARCH_MESSAGE_RECEIVE:
        case conversationTab.SEARCH_MESSAGE_SEND:
          return false;

        default:
          return false;
      }
    },
    [],
  );

  const getListLabel = async () => {
    const response = await apiFactory.labelApi.getList();
    if (response?.status === 200) {
      const labels = response?.data?.map((s) => {
        return { ...s, value: s?.labelId, label: s?.labelName };
      });
      setLabelList([...labels]);
    } else {
      openNotificationError(response?.message);
    }
  };

  useEffect(() => {
    getListLabel();
  }, []);

  useEffect(() => {
    if (location.pathname === "/conversation/draft-mail") {
      setSelectedConversation({ type: conversationType.DRAFT });
    }
    if (location.pathname === "/conversation") {
      setSelectedConversation(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      selectedConversation?.type === conversationType.PERSONAL &&
      user?.userId
    ) {
      const members = selectedConversation?.members || [];
      const personal = members.find((member) => member?.userId !== user.userId);
      setPersonalDetail(personal || null);
    }
  }, [selectedConversation, user?.userId]);

  useEffect(() => {
    const unreadDisplay = conversationList.reduce((total, conversation) => {
      if (conversation?.lastMessageType !== MESSAGE_STATUS.DISABLE_GROUP) {
        return total + (conversation?.unreadCount || 0);
      }
      return total;
    }, 0);

    updateFavicon(unreadDisplay + totalUnreadHidden);
    window?.chrome?.webview?.postMessage({
      type: "UPDATE_UNREAD",
      unreadCount: unreadDisplay + totalUnreadHidden,
    });
  }, [totalUnreadHidden, conversationList]);

  useEffect(() => {
    switch (activeKey) {
      /** normal case */
      case conversationTab.ALL:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            labelIds: conversationSearch?.labelIds,
            starred: conversationSearch?.starred,
          },
        });
        break;
      case conversationTab.UNREAD:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            isUnread: true,
            labelIds: conversationSearch?.labelIds,
            starred: conversationSearch?.starred,
          },
        });
        break;
      case conversationTab.CHAT:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            types: [
              conversationType.GROUP,
              conversationType.PERSONAL,
              conversationType.SELF,
              conversationType.SYSTEM,
            ],
            labelIds: conversationSearch?.labelIds,
            starred: conversationSearch?.starred,
          },
        });
        break;
      case conversationTab.NOTICE:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            types: [conversationType.NOTIFICATION],
            labelIds: conversationSearch?.labelIds,
            isNotification: true,
          },
        });
        break;
      case conversationTab.MESSAGE_RECEIVE:
      case conversationTab.MESSAGE_SEND:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            types: [conversationType.NOTIFICATION],
            labelIds: conversationSearch?.labelIds,
            isHost: activeKey === conversationTab.MESSAGE_SEND,
            starred: conversationSearch?.starred,
          },
        });
        break;
      /** search case */
      case conversationTab.CONTACT_CHAT:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          isSearch: true,
          search: {
            types: [
              conversationType.GROUP,
              conversationType.PERSONAL,
              conversationType.SELF,
              conversationType.SYSTEM,
            ],
            searchMemberName: conversationSearch?.textSearch,
            isPersonal: true,
          },
        });
        break;
      // case conversationTab.CONTACT_MESSAGE:
      case conversationTab.SEARCH_NOTICE:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            types: [conversationType.NOTIFICATION],
            searchMemberName: conversationSearch?.textSearch,
            isNotification: activeKey === conversationTab.SEARCH_NOTICE,
          },
        });
        break;
      case conversationTab.SEARCH_CHAT:
        fetchConversationsSearchMessageData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            searchContent: conversationSearch?.textSearch,
            type: [
              conversationType.GROUP,
              conversationType.PERSONAL,
              conversationType.SELF,
              conversationType.SYSTEM,
            ],
          },
        });
        break;
      case conversationTab.SEARCH_MESSAGE_RECEIVE:
      case conversationTab.SEARCH_MESSAGE_SEND:
        fetchConversationsSearchMailData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          direction: "DESC",
          search: {
            searchContent: conversationSearch?.textSearch,
            isHost: activeKey === conversationTab.SEARCH_MESSAGE_SEND,
          },
        });
        break;
      case conversationTab.CONTACT_MESSAGE:
        fetchConversationsSearchLetterContact({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          direction: "DESC",
          search: {
            personName: conversationSearch?.textSearch,
            isNotification: false,
          },
        });
        break;
      case conversationTab.NEW_SEARCH_MESSAGE_RECEIVE:
      case conversationTab.NEW_SEARCH_MESSAGE_SEND:
        fetchConversationsSearchLetterData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          direction: "DESC",
          search: {
            searchContent: conversationSearch?.textSearch,
            isHost: activeKey === conversationTab.NEW_SEARCH_MESSAGE_SEND,
            isSearchTitle: true,
          },
        });
        break;
      case conversationTab.SCHEDULE:
        fetchConversationsData({
          limit: CHATTING.CONVERSATION_LIMIT,
          maxTs: null,
          search: {
            // types: [conversationType.SCHEDULE],
            labelIds: conversationSearch?.labelIds,
            starred: conversationSearch?.starred,
            searchMemberName: conversationSearch?.textSearch,
            isNotification: activeKey === conversationTab.SEARCH_NOTICE,
            isSchedule: true,
          },
        });
        break;
      default:
        console.log("not exist this tab");
    }
  }, [activeKey, conversationSearch]);

  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);

  useEffect(() => {
    getConversation();
    setKeydownMenuOn(true);
  }, [selectedConversationId]);

  useEffect(() => {
    calculateConversationListHeight();
  }, [isSearching, windowHeight]);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;
      processEventData(eventData);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId, user?.userId, activeKey]);

  const values = useMemo(
    () => ({
      isMenu,
      labelSelectedId,
      isModalAddMember,
      isModalAddCC,
      setIsModalAddCC,
      isOpenModalConfirm,
      loading,
      isMoreMessage,
      messageSearch,
      conversationSearch,
      setConversationSearch,
      fetchConversationsData,
      fetchConversationsSearchMessageData,
      conversationList,
      conversationTs,
      selectedConversation,
      quotation,
      isMoreConversation,
      isMessageSearch,
      lastConversationTs,
      totalNotificationItem,
      setTotalNotificationItem,
      onChangeConversation,
      onChangeUnreadConversation,
      setLastConversationTs,
      setConversationTs,
      setSelectedConversation,
      setQuotation,
      setLoading,
      setIsMoreConversation,
      setIsMoreMessage,
      setMessageSearch,
      conversationRef,
      refreshChatMessage,
      setIsMessageSearch,
      setIsMenu,
      setLabelSelectedId,
      setIsModalAddMember,
      setIsOpenModalConfirm,
      onSearch,
      onBackToConversations,
      onChangeLabel,
      onChangeStar,
      openAddMemberModal,
      showModalAddCC,
      openModalConfirm,
      setActiveKey,
      activeKey,
      isSearching,
      setIsSearching,
      searchConversationList,
      setSearchConversationList,
      lastSearchConversationTs,
      setLastSearchConversationTs,
      isMoreSearchConversation,
      setIsMoreSearchConversation,
      searchConversationRef,
      setConversationList,
      conversationSearchText,
      setConversationSearchText,
      onChangeConversationTab,
      isConversationsLoading,
      setIsConversationsLoading,
      onCloseChatMessage,
      isOpenModalConfirmSaveEmail,
      setIsOpenModalConfirmSaveEmail,
      onChangeConversationBeforeCloseModalSavedEmail,
      updateConversationList,
      getConversation,
      isDiffHtmlMail,
      setIsDiffHtmlMail,
      isModalLabelGroup,
      setIsModalLabelGroup,
      showModalLabel,
      handleChangeConversation,
      refreshMailMessage,
      handleReloadConversation,
      setPersonalDetail,
      personalDetail,
      fetchMoreConversations,
      fetchMoreSearchChat,
      fetchMoreMailConversations,
      fetchMoreLetterContact,
      isAsRead,
      setIsAsRead,
      markAsRead,
      keydownMenuOn,
      setKeydownMenuOn,
      resetAndReloadConversations,
      onFocusConversationSearch,
      onUnFocusConversationSearch,
      selectedMailId,
      setSelectedMailId,
      selectedMessageId,
      setSelectedMessageId,
      panelStatus,
      setPanelStatus,
      mediaStorageType,
      setMediaStorageType,
      collapseMedia,
      setCollapseMedia,
      openCollapses,
      setOpenCollapses,
      mediaStorageLoading,
      loadMoreMediaStorage,
      fetchMediaStorageResources,
      appendToMediaStorage,
      appendToRecentCollapse: appendToMediaStorage,
      handleIncomingFiles,
      groupInfoResources,
      setGroupInfoResources,
      fetchGroupInfoResources,
      appendToGroupInfoResources,
      removeMediaFromStorage,
      handleUpdateConversationList,
      conversationHeight,
      jumpToSearchChat,
      labelList,
      setLabelList,
    }),
    [
      conversationSearch,
      isDiffHtmlMail,
      conversationList,
      selectedConversation,
      isMessageSearch,
      messageSearch,
      isMenu,
      labelSelectedId,
      isModalAddMember,
      isModalAddCC,
      isOpenModalConfirm,
      isMoreConversation,
      activeKey,
      quotation,
      isConversationsLoading,
      isOpenModalConfirmSaveEmail,
      isModalLabelGroup,
      isSearching,
      searchConversationList,
      lastSearchConversationTs,
      isMoreSearchConversation,
      conversationSearchText,
      personalDetail,
      isAsRead,
      keydownMenuOn,
      loading,
      isMoreMessage,
      conversationTs,
      lastConversationTs,
      totalNotificationItem,
      selectedMailId,
      selectedMessageId,
      panelStatus,
      mediaStorageType,
      collapseMedia,
      openCollapses,
      mediaStorageLoading,
      loadMoreMediaStorage,
      fetchMediaStorageResources,
      appendToMediaStorage,
      handleIncomingFiles,
      groupInfoResources,
      fetchGroupInfoResources,
      appendToGroupInfoResources,
      removeMediaFromStorage,
      handleUpdateConversationList,
      onChangeConversation,
      conversationHeight,
      labelList,
    ],
  );

  return <MenuContext.Provider value={values}>{children}</MenuContext.Provider>;
};
